import React, { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

const API_BASE = import.meta.env.VITE_API_URL || '/api';

export default function ForgotPasswordPage() {
  const [searchParams] = useSearchParams();
  const defaultRole = searchParams.get('role') === 'staff' ? 'staff' : 'owner';

  const [form, setForm] = useState({ email: '', role: defaultRole, slug: '' });
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState('');
  const [error, setError] = useState('');

  function handleChange(e) {
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
    setError('');
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.email) { setError('Email is required.'); return; }
    if (form.role === 'staff' && !form.slug) { setError('Restaurant ID is required for staff.'); return; }

    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_BASE}/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: form.email,
          role: form.role,
          ...(form.role === 'staff' ? { slug: form.slug } : {}),
        }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error || 'Something went wrong.'); return; }
      setMsg(data.message);
    } catch {
      setError('Network error. Is the backend running?');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="main-full">
      <div className="auth-container">
        <div className="auth-card">
          <div className="auth-logo">
            <h1>Roster<span>AI</span></h1>
            <p>Smart restaurant staff scheduling</p>
          </div>

          <h2 className="auth-title">Forgot Password</h2>
          <p className="auth-subtitle">Enter your email and we'll send a reset link</p>

          {msg ? (
            <div className="alert alert-success" style={{ marginBottom: 16 }}>{msg}</div>
          ) : (
            <>
              {error && <div className="alert alert-error">{error}</div>}
              <form onSubmit={handleSubmit}>
                <div className="form-group">
                  <label>I am a</label>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button
                      type="button"
                      className={`btn btn-sm ${form.role === 'owner' ? 'btn-primary' : 'btn-outline'}`}
                      style={{ flex: 1 }}
                      onClick={() => setForm(prev => ({ ...prev, role: 'owner', slug: '' }))}
                    >
                      Owner
                    </button>
                    <button
                      type="button"
                      className={`btn btn-sm ${form.role === 'staff' ? 'btn-primary' : 'btn-outline'}`}
                      style={{ flex: 1 }}
                      onClick={() => setForm(prev => ({ ...prev, role: 'staff' }))}
                    >
                      Staff
                    </button>
                  </div>
                </div>

                {form.role === 'staff' && (
                  <div className="form-group">
                    <label>Restaurant ID</label>
                    <input
                      type="text"
                      className="form-control"
                      name="slug"
                      value={form.slug}
                      onChange={handleChange}
                      placeholder="e.g. sushi-place"
                      autoComplete="off"
                    />
                    <div className="form-hint">Ask your manager for the restaurant ID</div>
                  </div>
                )}

                <div className="form-group">
                  <label>Email</label>
                  <input
                    type="email"
                    className="form-control"
                    name="email"
                    value={form.email}
                    onChange={handleChange}
                    placeholder="you@example.com"
                    autoComplete="email"
                    required
                  />
                </div>

                <button type="submit" className="btn btn-primary btn-block btn-lg" disabled={loading}>
                  {loading ? <span className="spinner" /> : 'Send Reset Link'}
                </button>
              </form>
            </>
          )}

          <div className="auth-footer">
            <Link to="/login">Back to Owner Login</Link>
            {' · '}
            <Link to="/staff/login">Staff Login</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
